import { useEffect, useRef } from "react";

import type { PresentationScene } from "./types";
import InfoPhoto from "./InfoPhoto";

interface InfoDialogProps {
  data: PresentationScene;
  open: boolean;
  onClose: () => void;
}

/** Text a fotografie úvodního okna se upravují zde, nezávisle na 3D scéně. */
export default function InfoDialog({ data, open, onClose }: InfoDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      id="presentation-info"
      className="info-dialog"
      ref={dialogRef}
      aria-labelledby="info-title"
      onClose={onClose}
    >
      <header className="info-header">
        <p className="eyebrow">O projektu</p>
        <button
          className="info-close"
          type="button"
          aria-label="Zavřít informace"
          onClick={() => dialogRef.current?.close()}
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div
        className="info-content"
        tabIndex={0}
        aria-label="Informace o projektu"
      >
        <h1 id="info-title">{data.title}</h1>
        <p>{data.artist}</p>
        <p className="info-lead">
          Plastika, fotografie a hledání společného tvaru v prostoru.
        </p>
        <p>
          Do prostorové rekonstrukce plastiky {data.title} jsem se pustil až po
          jejím rozlomení a pádu. Měl jsem k tomu dva důvody: ta socha se mi
          jednoduše líbí a zároveň pro mě byla její rekonstrukce skvělou
          technickou výzvou. Z fotografií stažených z internetu jsem vytvořil 3D
          model a v této prezentaci ukazuji, jak odpovídá jednotlivým pohledům.
        </p>

        <figure className="info-hero">
          <img
            src={data.photos[0].image}
            alt={`Plastika ${data.title}: ${data.photos[0].title}`}
            width={data.photos[0].width}
            height={data.photos[0].height}
          />
          <figcaption>
            Zdroj:{" "}
            <a
              href="https://www.drobnepamatky.cz/node/24585"
              target="_blank"
              rel="noreferrer"
            >
              Drobné památky
            </a>
          </figcaption>
        </figure>

        <h2>Jak vzniká tvar z fotografií</h2>
        <p>
          Fotografií jsem měl k dispozici příliš málo na použití klasických
          fotogrammetrických algoritmů. Musel jsem proto vytvořit vlastní řešení
          přímo na míru Rovnováze.
        </p>
        <p>
          Na snímcích byly ručně označeny odpovídající body a hrany plastiky. Z
          těchto anotací se společně odhadly pozice kamer a prostorové body. Na
          ně navázala rekonstrukce čtyř hladkých hran, mezi kterými je vytvořen
          povrch modelu. Červená, zelená, modrá a purpurová barva pomáhají
          sledovat stejnou hranu napříč různými pohledy.
        </p>
        <p>
          Fotografie jsou v prostoru umístěné podle nalezených kamer. Při
          přiblížení k jednotlivému snímku tak můžete porovnat model s jeho
          fotografickou předlohou. Ortofotomapa pod scénou slouží k orientaci v
          okolí plastiky.
        </p>

        <div className="info-gallery">
          {data.photos.slice(1, 3).map((photo) => (
            <figure key={photo.id}>
              <InfoPhoto photo={photo} edges={data.model.edges} />
              <figcaption>
                Zdroj:{" "}
                <a
                  href={
                    photo.id === "expresarchiv_001"
                      ? "https://www.expres.cz/zpravy/cerv-dobyvatel-padl-betonovy-zazrak-u-barrandovskeho-mostu-vznikl-navzdory-inzenyrum.A260717_160605_dx-zpravy_bpet"
                      : "https://www.horydoly.cz/horolezci/video-cerv-dobyvatel.html"
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {photo.id === "expresarchiv_001"
                    ? "Expres.cz / archiv Josefa Klimeše"
                    : photo.title}
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="info-legend">
          Kroužky a přerušované čáry: ruční anotace. Plné barevné čáry: projekce
          hran výsledného 3D modelu.
        </p>

        <h2>Od modelu k 3D tisku</h2>
        <p>
          K rekonstruovanému tvaru jsem následně v CADu ručně dokreslil
          podstavec podle informací dostupných z internetu. Hotový model včetně
          podstavce jsem si pak vytiskl na 3D tiskárně v měřítku 1:100.
        </p>
        <div className="info-gallery">
          <figure>
            <img
              src="info/cad.png"
              alt="Model Rovnováhy s ručně dokresleným podstavcem v CADu"
              width={1021}
              height={761}
              loading="lazy"
            />
            <figcaption>Model s doplněným podstavcem v CADu.</figcaption>
          </figure>
          <figure>
            <img
              src="info/3dtisk.png"
              alt="Rovnováha s podstavcem vytištěná na 3D tiskárně v měřítku 1:100"
              width={1536}
              height={1024}
              loading="lazy"
            />
            <figcaption>Hotový 3D výtisk v měřítku 1:100.</figcaption>
          </figure>
        </div>

        <h2>Prohlédněte si rekonstrukci</h2>
        <p>
          Zavřete toto okno a nechte se provést jednotlivými fotografiemi.
          Průlet můžete pozastavit tlačítkem nebo mezerníkem. Po pozastavení
          scénu otáčejte tažením a přibližujte kolečkem; na dotykové obrazovce
          použijte prsty. Šipkami přejdete na předchozí nebo další pohled.
        </p>
        <p>K těmto informacím se kdykoliv vrátíte tlačítkem „Info“.</p>

        <h2>O mně</h2>
        <p>
          Říkám si bkralik a jsem nadšenec do vědy, techniky a taky trochu do
          moderního umění 🙂. Pokud máte k rekonstrukci dotazy nebo mi chcete
          něco napsat, ozvěte se na{" "}
          <a href="mailto:bkralik@bkralik.cz">bkralik@bkralik.cz</a>.
        </p>
      </div>
    </dialog>
  );
}
